/**
 * PlatformFeeNotificationModal
 *
 * Shown once per session when the client has uploaded leads (or queued
 * unconfirmed rows for deferred verification) but does NOT have an active
 * platformPaidUntil timestamp. Their verified leads will populate normally,
 * but Send Calls will be blocked until they pay the $500 platform fee.
 *
 * Reads `pendingPlatformFeeNotification` on the client doc (server-side
 * check in createPhoneBatchJob / addDeferredVerification writes it).
 * Dismissing calls `dismissPlatformFeeNotification` which clears the flag.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Props {
  source: 'verification' | 'deferred' | 'good_full_only' | 'send_attempt';
  onClose: () => void;
  onBuyPlatform?: () => void;
}

export function PlatformFeeNotificationModal({ source, onClose, onBuyPlatform }: Props) {
  const [dismissing, setDismissing] = useState(false);

  const sourceLine =
    source === 'verification'
      ? 'Your verification job is paid and in progress.'
      : source === 'deferred'
      ? 'Your unverified leads are saved to the queue.'
      : source === 'send_attempt'
      ? 'Sending is blocked until the platform fee is active.'
      : 'Your verified leads are saved to your account.';

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const fn = httpsCallable(functions, 'dismissPlatformFeeNotification');
      await fn({});
    } catch (err) {
      console.error('Failed to dismiss platform-fee notification:', err);
    } finally {
      onClose();
    }
  };

  const handleBuy = async () => {
    if (onBuyPlatform) {
      // Clear the flag server-side so the modal doesn't re-pop on return from Stripe
      try {
        const fn = httpsCallable(functions, 'dismissPlatformFeeNotification');
        await fn({});
      } catch (err) {
        console.error('Failed to dismiss platform-fee notification:', err);
      }
      onBuyPlatform();
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={contentStyle}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔔</div>
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>Platform fee still required</h2>
        </div>

        <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 12px' }}>
          {sourceLine}
        </p>
        <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 16px' }}>
          Before you can send calls, you&rsquo;ll need to pay the{' '}
          {/* No dollar figure here: a client with a standing discount pays less
              than list, and the Buy Credits screen shows their real price. */}
          <strong style={{ color: 'var(--gold)' }}>platform fee</strong> (covers 30 days of access).
          Your leads are <strong>already saved</strong> and will show up on your dashboard &mdash; they&rsquo;re just gated from
          sending until the fee is paid.
        </p>

        <div style={infoBoxStyle}>
          <strong style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>Why this exists</strong>
          <ul style={{ margin: '8px 0 0', padding: '0 0 0 20px', color: 'var(--gray-200)', fontSize: '0.85rem', lineHeight: 1.6 }}>
            <li>Covers your AI voice agent, phone numbers, and infrastructure for 30 days</li>
            <li>First-time platform payers get 2,500 free call credits as a welcome bonus</li>
            <li>Good &amp; verified leads will remain available while you sort this out</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="btn btn-secondary"
          >
            {dismissing ? 'Dismissing...' : 'Remind me later'}
          </button>
          {onBuyPlatform && (
            <button
              onClick={handleBuy}
              disabled={dismissing}
              className="btn btn-primary"
            >
              Pay platform fee
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const contentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '520px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};

const infoBoxStyle: React.CSSProperties = {
  padding: '14px 16px', borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.08)',
  border: '1px solid rgba(200, 168, 78, 0.2)',
};
